import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box,
    IconButton,
    TextField,
    Snackbar,
    Alert,
    Pagination,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';

// Define the type for an unmatched book based on the backend schema
interface UnmatchedBook {
    id: number;
    cover_url: string;
    binding_type: string;
    llm_description: string | null;
    asin: string | null;
    created_at: string; // Assuming string format from backend
    processed: number | null;
}

/**
 * Toggle this flag to switch between production and development API endpoints.
 * Set to true for production, false for local development.
 */
const IS_PRODUCTION = true; // Set to true for production as per user feedback

const API_BASE_URL = IS_PRODUCTION
  ? 'https://api.covervisionai.com/api/v1'
  : 'http://localhost:8000/api/v1';


const UnmatchedBooks: React.FC = () => {
    const { getToken } = useAuth();
    const [unmatchedBooks, setUnmatchedBooks] = useState<UnmatchedBook[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [currentAsin, setCurrentAsin] = useState<string>('');
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [page, setPage] = useState<number>(1);
    const itemsPerPage = 50;

    const fetchUnmatchedBooks = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getToken({ template: "covervision-backend" });
             if (!token) {
                throw new Error('Authentication token not available.');
            }
            const response = await axios.get<UnmatchedBook[]>(`${API_BASE_URL}/unmatched-books/list?asin_null=true`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            // Check if the response data is an array before setting the state
            if (Array.isArray(response.data)) {
                setUnmatchedBooks(response.data);
            } else {
                console.error('API response data is not an array:', response.data);
                setError('Received unexpected data format from the server.');
                setUnmatchedBooks([]); // Ensure unmatchedBooks is always an array
                setSnackbarMessage('Received unexpected data format from the server.');
            }
        } catch (err) {
            console.error('Error fetching unmatched books:', err);
            setError('Failed to fetch unmatched books.');
            setSnackbarMessage('Failed to fetch unmatched books.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    }, [getToken]); // Only depend on getToken

    useEffect(() => {
        fetchUnmatchedBooks();
    }, [fetchUnmatchedBooks]); // fetchUnmatchedBooks is now stable due to useCallback

    const handleEditClick = (book: UnmatchedBook) => {
        setEditingId(book.id);
        setCurrentAsin(book.asin || '');
    };

    const handleSaveClick = async (bookId: number) => {
        if (!currentAsin) {
            setSnackbarMessage('ASIN cannot be empty.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        try {
            const token = await getToken({ template: "covervision-backend" });
            if (!token) {
                throw new Error('Authentication token not available.');
            }
            
            // Use POST endpoint instead of PATCH to avoid CORS issues
            await axios.post(`${API_BASE_URL}/unmatched-books/${bookId}/update`, {
                asin: currentAsin,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setSnackbarMessage('ASIN updated successfully.');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setEditingId(null);
            setCurrentAsin('');
            fetchUnmatchedBooks(); // Refresh data after successful update
        } catch (err) {
            console.error('Error updating ASIN:', err);
            setSnackbarMessage('Failed to update ASIN.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleDeleteClick = async (bookId: number) => {
        if (window.confirm('Are you sure you want to delete this unmatched book?')) {
            try {
                const token = await getToken({ template: "covervision-backend" });
                if (!token) {
                    throw new Error('Authentication token not available.');
                }
                
                // Use POST endpoint instead of DELETE to avoid CORS issues
                await axios.post(`${API_BASE_URL}/unmatched-books/${bookId}/delete`, {}, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setSnackbarMessage('Book deleted successfully.');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                fetchUnmatchedBooks(); // Refresh data after successful deletion
            } catch (err) {
                console.error('Error deleting book:', err);
                setSnackbarMessage('Failed to delete book.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Unmatched Books
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="unmatched books table">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Cover</TableCell>
                                <TableCell>Binding</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Created At</TableCell>
                                <TableCell>ASIN</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {unmatchedBooks
                                .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                                .map((book) => (
                                <TableRow
                                    key={book.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell component="th" scope="row">
                                        {book.id}
                                    </TableCell>
                                    <TableCell>
                                        {book.cover_url ? (
                                            <img
                                                src={book.cover_url}
                                                alt={`Cover for ID ${book.id}`}
                                                style={{ maxWidth: '100px', height: 'auto' }}
                                            />
                                        ) : (
                                            'No Cover'
                                        )}
                                    </TableCell>
                                    <TableCell>{book.binding_type}</TableCell>
                                    <TableCell>{book.llm_description}</TableCell>
                                    <TableCell>{new Date(book.created_at).toLocaleString()}</TableCell>
                                    <TableCell>
                                        {editingId === book.id ? (
                                            <TextField
                                                value={currentAsin}
                                                onChange={(e) => setCurrentAsin(e.target.value)}
                                                size="small"
                                                variant="outlined"
                                                placeholder="Enter ASIN"
                                                autoFocus
                                            />
                                        ) : (
                                            <Box 
                                                onClick={() => handleEditClick(book)} 
                                                sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }}
                                            >
                                                {book.asin || 'NULL (Click to edit)'}
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === book.id ? (
                                            <IconButton color="primary" onClick={() => handleSaveClick(book.id)} aria-label="save">
                                                <SaveIcon />
                                            </IconButton>
                                        ) : (
                                            <IconButton 
                                                color="primary" 
                                                onClick={() => handleEditClick(book)} 
                                                aria-label="edit"
                                                disabled={loading}
                                            >
                                                <SaveIcon />
                                            </IconButton>
                                        )}
                                        <IconButton 
                                            color="error" 
                                            onClick={() => handleDeleteClick(book.id)} 
                                            disabled={loading}
                                            aria-label="delete"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading && !error && unmatchedBooks.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                    <Pagination 
                        count={Math.ceil(unmatchedBooks.length / itemsPerPage)} 
                        page={page} 
                        onChange={handlePageChange} 
                        color="primary" 
                    />
                </Box>
            )}

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default UnmatchedBooks;
